import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Terminal, Play, Download, Copy, 
  Cpu, Zap, ChevronRight, 
  Layers, Package, Activity, ShoppingCart,
  Flame, Globe, HelpCircle, AlertTriangle, BookOpen
} from 'lucide-react';
import { api } from '../lib/api';
import { parseIntelHex } from '../lib/intelHex';
import { ProjectManager } from '../lib/projectManager';
import Schematics from './Schematics';
import GuidedTour, { TourStep } from '../components/GuidedTour';

const TOUR_STEPS: TourStep[] = [
  { targetId: 'tour-ai-pane', title: 'The AI Brain', content: 'Describe what you want to build. ElectrAI writes the code for you instantly.', position: 'right' },
  { targetId: 'tour-code-editor', title: 'Your Workbench', content: 'Review and modify the AI-generated code here. You have full control.', position: 'left' },
  { targetId: 'tour-diagrams-tab', title: 'Live Schematics', content: 'Click here to find Arduino/ESP32 pinouts and datasheets without leaving your code.', position: 'bottom' },
  { targetId: 'tour-flash-btn', title: 'Flash via Web Serial', content: 'Plug your board into USB. Click here to flash the code directly from Chrome!', position: 'bottom' }
];

export default function Studio() {
    const location = useLocation();
    const hasHandledQuery = useRef(false);
    const [code, setCode] = useState('');
    const [chatHistory, setChatHistory] = useState<Array<{role: 'user' | 'assistant', content: string}>>([]);
    const [isWorkspaceLoaded, setIsWorkspaceLoaded] = useState(false);
    const [isTourOpen, setIsTourOpen] = useState(false);

  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'editor' | 'n8n' | 'wiring' | 'analyzer' | 'bom' | 'power' | 'fleet' | 'rescue' | 'driver' | 'fab' | 'flasher'>('editor');

  const [port, setPort] = useState<any>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [serialLog, setSerialLog] = useState<string[]>([]);
  const [logText, setLogText] = useState('');
  const [analyzingLog, setAnalyzingLog] = useState(false);
  const [diagnosis, setDiagnosis] = useState('');
  
  const [rescueStep, setRescueStep] = useState(0);
  const [rescueAnswers, setRescueAnswers] = useState<any>({});
  const [rescueLoading, setRescueLoading] = useState(false);
  const [rescueResult, setRescueResult] = useState<any>(null);
  
  const [powerLoading, setPowerLoading] = useState(false);
  const [powerReport, setPowerReport] = useState<any>(null);
  
  const [bomLoading, setBomLoading] = useState(false);
  const [bomList, setBomList] = useState<Array<{ name: string, qty: number, estPrice: string, type: string, description: string }>>([]);

  const [_wiringLoading, setWiringLoading] = useState(false);
  const [wiringBlueprint, setWiringBlueprint] = useState<any>(null);
  
  const [n8nNodes, setN8nNodes] = useState<string>('');
  const [n8nLoading, setN8NLoading] = useState(false);

  const [fabLoading, setFabLoading] = useState(false);
  const [fabPackage, setFabPackage] = useState<any>(null);

  const [flashing, setFlashing] = useState(false);
  const [flashProgress, setFlashProgress] = useState(0);
  const [firmwareFile, setFirmwareFile] = useState<File | null>(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const state = ProjectManager.loadWorkspace();
    setCode(state.code);
    setChatHistory(state.chatHistory);
    setIsWorkspaceLoaded(true);

    // Show tour on first visit
    if (!localStorage.getItem('diy_mech_tour_seen')) {
        setTimeout(() => setIsTourOpen(true), 1000);
    }
  }, []);

  useEffect(() => {
      const initialQuery = location.state?.initialQuery;
      if (initialQuery && !hasHandledQuery.current && isWorkspaceLoaded) {
          hasHandledQuery.current = true;
          setPrompt(initialQuery);
          // Auto-trigger generation
          setTimeout(() => handleGenerate(initialQuery), 100);
          // Clear state so refresh doesn't trigger it again
          window.history.replaceState({}, document.title);
      }
  }, [location.state, isWorkspaceLoaded]);

  useEffect(() => {
    if (isWorkspaceLoaded) {
      ProjectManager.saveWorkspace({ code, chatHistory });
    }
  }, [code, chatHistory, isWorkspaceLoaded]);


  const handleGenerate = async (overridePrompt?: string) => {
    const finalPrompt = overridePrompt || prompt;
    if (!finalPrompt.trim()) return;
    
    const userMessage = { role: 'user' as const, content: finalPrompt };
    setChatHistory(prev => [...prev, userMessage]);
    setLoading(true);
    if (!overridePrompt) setPrompt(''); // Clear input if it wasn't an override
    
    try {
      const response = await api.studio.architect(finalPrompt);
      const newCode = response.code;
      
      const aiMessage = { 
        role: 'assistant' as const, 
        content: `I have generated the code. You can apply it to your editor.\n\n\`\`\`cpp\n${newCode}\n\`\`\``
      };
      
      setChatHistory(prev => [...prev, aiMessage]);
      setCode(newCode); // Optionally auto-apply, or let user click a button later.
      setActiveTab('editor'); // Switch to editor automatically
    } catch (e) {
      setChatHistory(prev => [...prev, { role: 'assistant' as const, content: 'Sorry, I encountered an error generating that.' }]);
      alert('Generation failed. Please try again.');
    } finally {
      setLoading(false);
      setPrompt('');
    }
  };

  const handleGenerateBOM = async () => {
    setBomLoading(true);
    try {
      const parsed = await api.studio.generateBOM(code);
      setBomList(Array.isArray(parsed) ? parsed : []);
    } catch (e) {
      alert("Failed to analyze Bill of Materials. Please try again.");
    } finally {
      setBomLoading(false);
    }
  };

  const handleAnalyzePower = async () => {
    setPowerLoading(true);
    try {
      const report = await api.studio.analyzePower(code);
      setPowerReport(report);
    } catch (e) {
      alert("Power profile calculation failed.");
    } finally {
      setPowerLoading(false);
    }
  };

  const handleRescueSubmit = async () => {
    setRescueLoading(true);
    try {
      const result = await api.studio.rescueAnalyze(code, rescueAnswers.log || '', rescueAnswers);
      setRescueResult(result);
    } catch (e) {
      alert("Rescue analysis failed.");
    } finally {
      setRescueLoading(false);
    }
  };

  const handleAnalyzeLog = async () => {
    if (!logText.trim()) return;
    setAnalyzingLog(true);
    try {
      // Send both the current code and the crash log to the AI
      const analysisPrompt = `Context Code:\n${code}\n\nSerial Monitor / Crash Log:\n${logText}\n\nAnalyze why this is crashing or failing. Give a direct, punchy, diagnostic explanation and the exact line or hardware component to fix.`;
      const response = await api.chat(analysisPrompt);
      setDiagnosis(response.content);
    } catch (e) {
      setDiagnosis("Analysis failed. Communications disrupted.");
    } finally {
      setAnalyzingLog(false);
    }
  };

  const _handleGenerateWiring = async () => {
    setWiringLoading(true);
    try {
      const blueprint = await api.studio.generateWiring(code);
      setWiringBlueprint(blueprint);
    } catch (e) {
      alert("Wiring blueprint generation failed. Try again.");
    } finally {
      setWiringLoading(false);
    }
  };
  void _handleGenerateWiring;

  const handleGenerateFab = async () => {
    setFabLoading(true);
    try {
      const result = await api.studio.generateFab(code, wiringBlueprint);
      setFabPackage(result);
    } catch (e) {
      alert("PCB Fab generation failed.");
    } finally {
      setFabLoading(false);
    }
  };

  const handleGenerateN8N = async () => {
    setN8NLoading(true);
    try {
      const nodes = await api.studio.generateN8N(code);
      setN8nNodes(JSON.stringify(nodes, null, 2));
    } catch (e) {
      alert("Failed to generate n8n Bridge package.");
    } finally {
      setN8NLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const serialPort = await (navigator as any).serial.requestPort();
      await serialPort.open({ baudRate: 115200 });
      setPort(serialPort);
      setIsConnected(true);
      startReadLoop(serialPort);
    } catch (err) {
      console.error("Connection failed:", err);
    }
  };

  const startReadLoop = async (sp: any) => {
    const textDecoder = new TextDecoderStream();
    sp.readable.pipeTo(textDecoder.writable);
    const reader = textDecoder.readable.getReader();
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (value) {
          setSerialLog(prev => [...prev.slice(-49), value]);
        }
      }
    } catch (e) {
      setIsConnected(false);
    }
  };

  const dtrReset = async () => {
    if (!port) return;
    await port.setSignals({ dataTerminalReady: false });
    await new Promise(r => setTimeout(r, 100));
    await port.setSignals({ dataTerminalReady: true });
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    alert('Code copied to clipboard!');
  };

  const downloadIno = () => {
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'project.ino';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFlashFirmware = async () => {
    if (!firmwareFile || !port) {
      alert("Please connect to a device and select a firmware file first.");
      return;
    }

    setFlashing(true);
    setFlashProgress(0);
    setSerialLog(prev => [...prev, `[FLASH] PREPARING: ${firmwareFile.name}...`]);

    try {
      // 1. DTR Reset to trigger bootloader (standard for Arduino/ESP)
      setSerialLog(prev => [...prev, `[FLASH] Resetting device via DTR...`]);
      await dtrReset();
      await new Promise(r => setTimeout(r, 500));

      const isHex = firmwareFile.name.toLowerCase().endsWith('.hex');
      let content: Uint8Array;

      if (isHex) {
        setSerialLog(prev => [...prev, `[FLASH] Parsing Intel HEX file...`]);
        const text = await firmwareFile.text();
        content = parseIntelHex(text);
        setSerialLog(prev => [...prev, `[FLASH] HEX Parsed: ${content.length} bytes of binary data extracted.`]);
      } else {
        setSerialLog(prev => [...prev, `[FLASH] Reading raw binary file...`]);
        const buffer = await firmwareFile.arrayBuffer();
        content = new Uint8Array(buffer);
      }

      if (content.length === 0) {
        throw new Error("File content is empty or invalid.");
      }

      const writer = port.writable.getWriter();
      const chunkSize = 1024;
      
      setSerialLog(prev => [...prev, `[FLASH] Beginning deployment of ${content.length} bytes...`]);
      
      for (let i = 0; i < content.length; i += chunkSize) {
        const chunk = content.slice(i, i + chunkSize);
        await writer.write(chunk);
        
        const progress = Math.round((Math.min(i + chunkSize, content.length) / content.length) * 100);
        setFlashProgress(progress);
        
        if (i % (chunkSize * 10) === 0 || progress === 100) {
           setSerialLog(prev => [...prev.slice(-49), `[FLASH] DEPLOYING: ${progress}% (${Math.round((i + chunk.length) / 1024)}K / ${Math.round(content.length / 1024)}K)`]);
        }
      }

      await writer.releaseLock();
      setFlashProgress(100);
      setSerialLog(prev => [...prev, `[FLASH] SUCCESS: ${firmwareFile.name} deployed. Waiting for device reboot...`]);
      
      // Optional: Another reset after flash to jump to app
      await dtrReset();
      
      alert("Firmware flashed successfully!");
    } catch (err) {
      console.error("Flash failed:", err);
      setSerialLog(prev => [...prev, `[FLASH] ERROR: ${err}`]);
      alert(`Flash failed: ${err}`);
    } finally {
      setFlashing(false);
    }
  };


  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: 'calc(100dvh - 64px)', 
      background: '#050912',
      color: 'var(--text-primary)',
      overflow: 'hidden'
    }}>
      
      {/* Toolbar */}
      <div style={{ 
        minHeight: '56px', 
        borderBottom: '1px solid var(--border-color)', 
        display: 'flex', 
        alignItems: 'center', 
        padding: '0 1rem',
        justifyContent: 'flex-start',
        background: 'rgba(5,9,18,0.95)',
        zIndex: 10,
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        gap: '1rem',
        scrollbarWidth: 'none'
      }} className="studio-toolbar no-scrollbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--color-primary)' }}>
            <Terminal size={14} />
            <span style={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>STUDIO</span>
          </div>
          <div style={{ height: '16px', width: '1px', background: 'var(--border-color)' }} />
          <div style={{ display: 'flex', gap: '0.25rem', overflowX: 'auto', paddingRight: '1rem' }} className="no-scrollbar">
            <button 
              onClick={() => setActiveTab('editor')}
              style={{ 
                background: activeTab === 'editor' ? 'rgba(0,255,136,0.1)' : 'transparent',
                border: 'none', color: activeTab === 'editor' ? 'var(--color-primary)' : 'var(--text-muted)',
                padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer'
              }}>Editor</button>
            <button 
              onClick={() => setActiveTab('n8n')}
              style={{ 
                background: activeTab === 'n8n' ? 'rgba(168,85,247,0.1)' : 'transparent',
                border: 'none', color: activeTab === 'n8n' ? 'var(--color-purple)' : 'var(--text-muted)',
                padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer'
              }}>Autobridge (n8n)</button>
            <button 
              id="tour-diagrams-tab"
              onClick={() => setActiveTab('wiring')}
              style={{ 
                background: activeTab === 'wiring' ? 'rgba(0,207,255,0.1)' : 'transparent',
                border: 'none', color: activeTab === 'wiring' ? 'var(--color-secondary)' : 'var(--text-muted)',
                padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer'
              }}>Diagrams</button>
            <button 
              onClick={() => setActiveTab('analyzer')}
              style={{ 
                background: activeTab === 'analyzer' ? 'rgba(255,51,102,0.1)' : 'transparent',
                border: 'none', color: activeTab === 'analyzer' ? '#ff3366' : 'var(--text-muted)',
                padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.3rem'
              }}>
                <Activity size={12} /> Analyzer
            </button>
            <button 
              onClick={() => setActiveTab('bom')}
              style={{ 
                background: activeTab === 'bom' ? 'rgba(255,165,0,0.1)' : 'transparent',
                border: 'none', color: activeTab === 'bom' ? 'var(--color-warning, #ffaa00)' : 'var(--text-muted)',
                padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.3rem'
              }}>
                <ShoppingCart size={12} /> BOM Wizard
            </button>
            <button 
              onClick={() => setActiveTab('power')}
              style={{ 
                background: activeTab === 'power' ? 'rgba(255,85,0,0.1)' : 'transparent',
                border: 'none', color: activeTab === 'power' ? '#ff5500' : 'var(--text-muted)',
                padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.3rem'
              }}>
                <Flame size={12} /> Will It Burn?
            </button>
            <button 
              onClick={() => setActiveTab('fleet')}
              style={{ 
                background: activeTab === 'fleet' ? 'rgba(0,180,255,0.1)' : 'transparent',
                border: 'none', color: activeTab === 'fleet' ? 'var(--color-secondary)' : 'var(--text-muted)',
                padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.3rem'
              }}>
                <Globe size={12} /> OTA Fleet
            </button>
            <button 
              onClick={() => setActiveTab('rescue')}
              style={{ 
                background: activeTab === 'rescue' ? 'rgba(255,255,255,0.1)' : 'transparent',
                border: 'none', color: activeTab === 'rescue' ? 'var(--text-primary)' : 'var(--text-muted)',
                padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.3rem'
              }}>
                <HelpCircle size={12} /> Bench Rescue
            </button>
            <button 
              onClick={() => setActiveTab('driver')}
              style={{ 
                background: activeTab === 'driver' ? 'rgba(255,170,0,0.1)' : 'transparent',
                border: 'none', color: activeTab === 'driver' ? '#ffaa00' : 'var(--text-muted)',
                padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.3rem'
              }}>
                <BookOpen size={12} /> Driver Lab
            </button>
            <button 
              onClick={() => setActiveTab('fab')}
              style={{ 
                background: activeTab === 'fab' ? 'rgba(0,255,255,0.1)' : 'transparent',
                border: 'none', color: activeTab === 'fab' ? 'var(--color-secondary)' : 'var(--text-muted)',
                padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.3rem'
              }}>
                <Layers size={12} /> Fab Hub
            </button>
            <button 
              onClick={() => setActiveTab('flasher')}
              style={{ 
                background: activeTab === 'flasher' ? 'rgba(0,255,136,0.1)' : 'transparent',
                border: 'none', color: activeTab === 'flasher' ? 'var(--color-primary)' : 'var(--text-muted)',
                padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.3rem'
              }}>
                <Cpu size={12} /> Flasher
            </button>

            <button 
              onClick={() => {
                alert("Augmented Reality Mode requires ElectrAI Pro. Initializing hardware handshake...");
              }}
              style={{ 
                background: 'rgba(0,255,136,0.05)',
                border: '1px solid rgba(0,255,136,0.15)', 
                color: 'var(--color-primary)',
                padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.3rem',
                animation: 'flicker 4s infinite'
              }}>
                <Zap size={12} /> AR Mode
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0, paddingLeft: '0.5rem', borderLeft: '1px solid var(--border-color)', alignItems: 'center' }}>
          {isMobile && (
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="studio-btn"
              style={{
                background: sidebarOpen ? 'rgba(0,255,136,0.1)' : 'transparent',
                color: sidebarOpen ? 'var(--color-primary)' : 'var(--text-muted)',
                borderColor: sidebarOpen ? 'var(--color-primary)' : 'var(--border-color)',
                width: 'auto', padding: '0 0.5rem'
              }}
            >
              <Zap size={14} /> AI
            </button>
          )}
          <div id="tour-flash-btn" style={{ display: 'flex', gap: '0.4rem' }}>
             <button 
               onClick={handleConnect} 
            className="studio-btn" 
            style={{ 
              background: isConnected ? 'rgba(0,255,136,0.1)' : 'var(--bg-surface)',
              borderColor: isConnected ? 'var(--color-primary)' : 'var(--border-color)',
              width: 'auto', padding: '0 0.75rem', gap: '0.4rem', color: isConnected ? 'var(--color-primary)' : 'var(--text-muted)'
            }}
          >
            <Cpu size={14} /> {isConnected ? 'SYNC' : 'CON'}
          </button>
          <button onClick={copyToClipboard} className="studio-btn" title="Copy Code"><Copy size={14} /></button>
          <button onClick={downloadIno} className="studio-btn" title="Download .ino" style={{ display: isMobile ? 'none' : 'flex' }}><Download size={14} /></button>
          <button 
            onClick={dtrReset}
            className="studio-btn" 
            style={{ background: 'var(--color-primary)', color: '#050912', border: 'none' }} 
            title="Flash"
          >
            <Play size={14} />
          </button>
          </div>
        </div>
      </div>


      {/* Main Content Area */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
        
        {/* Left Side: AI Assistant */}
        <div id="tour-ai-pane" style={{ 
          width: isMobile ? '100%' : '380px', 
          position: isMobile ? 'absolute' : 'relative',
          top: 0, left: 0, bottom: 0,
          borderRight: '1px solid var(--border-color)', 
          display: 'flex', 
          flexDirection: 'column', 
          background: 'rgba(5,9,18,0.98)',
          zIndex: 40,
          transform: isMobile && !sidebarOpen ? 'translateX(-110%)' : 'translateX(0)',
          transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: isMobile && sidebarOpen ? '0 0 100px rgba(0,0,0,0.8)' : 'none'
        }}>

          <div style={{ padding: '1.25rem', flex: 1, overflowY: 'auto' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <Zap size={16} color="var(--color-primary)" />
                <h3 style={{ fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>AI Script Builder</h3>
              </div>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                Describe the logic you want for your Arduino or ESP32 project. ElectrAI will write the code and provide n8n node suggestions.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', paddingBottom: '1rem' }}>
              {chatHistory.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {[
                    "Blink LED when a button is pressed",
                    "Read DHT22 and send data via MQTT",
                    "Control a Servo with a potentiometer",
                    "Simple ESP32 Web Server with 2 buttons"
                  ].map(s => (
                    <button 
                      key={s} 
                      onClick={() => setPrompt(s)}
                      style={{ 
                        background: 'var(--bg-surface-2)', border: '1px solid var(--border-color)', borderRadius: '8px', 
                        padding: '0.6rem 0.8rem', textAlign: 'left', fontSize: '0.75rem', color: 'var(--text-secondary)',
                        cursor: 'pointer', transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(0,255,136,0.3)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
                    >{s}</button>
                  ))}
                </div>
              ) : (
                chatHistory.map((msg, i) => (
                  <div key={i} style={{ 
                    alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    background: msg.role === 'user' ? 'rgba(0,207,255,0.1)' : 'rgba(0,255,136,0.05)',
                    border: `1px solid ${msg.role === 'user' ? 'rgba(0,207,255,0.3)' : 'rgba(0,255,136,0.2)'}`,
                    borderRadius: '12px',
                    padding: '1rem',
                    maxWidth: '95%',
                    fontSize: '0.85rem',
                    color: 'var(--text-primary)',
                    lineHeight: 1.5,
                  }}>
                    <div style={{ fontWeight: 700, marginBottom: '0.5rem', color: msg.role === 'user' ? 'var(--color-secondary)' : 'var(--color-primary)' }}>
                      {msg.role === 'user' ? 'You' : 'ElectrAI'}
                    </div>
                    
                    {msg.content.includes('```cpp') ? (
                       <div>
                          <p>{msg.content.split('```cpp')[0]}</p>
                          <div style={{ marginTop: '0.75rem', background: '#000', padding: '0.75rem', borderRadius: '8px', overflowX: 'auto', fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: '#a6accd' }}>
                             {msg.content.split('```cpp')[1].split('```')[0].trim()}
                          </div>
                          <button
                            onClick={() => {
                               setCode(msg.content.split('```cpp')[1].split('```')[0].trim());
                               setActiveTab('editor');
                            }}
                            className="apply-editor-btn"
                            style={{ 
                               marginTop: '0.75rem', background: 'var(--color-primary)', color: '#000', border: 'none', 
                               borderRadius: '4px', padding: '0.4rem 0.75rem', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                               boxShadow: '0 0 10px rgba(0, 255, 136, 0.4)', transition: 'all 0.2s ease'
                            }}
                          >
                             Apply to Editor
                          </button>
                       </div>
                    ) : (
                       <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                    )}
                  </div>
                ))
              )}
              {loading && (
                 <div style={{ color: 'var(--color-primary)', fontSize: '0.85rem', animation: 'pulse 1.5s infinite' }}>
                   ElectrAI is thinking...
                 </div>
              )}
            </div>

          <div style={{ padding: '1.25rem', borderTop: '1px solid var(--border-color)', background: 'rgba(5,9,18,0.6)' }}>
             <div style={{ position: 'relative' }}>
                <textarea 
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  placeholder="Ask ElectrAI to build a script..."
                  style={{ 
                    width: '100%', minHeight: '80px', background: 'var(--bg-surface)', border: '1px solid var(--border-color)',
                    borderRadius: '10px', padding: '0.75rem', color: 'var(--text-primary)', fontSize: '0.85rem',
                    outline: 'none', resize: 'none'
                  }}
                />
                <button 
                  onClick={() => handleGenerate()}
                  disabled={loading || !prompt.trim()}
                  style={{ 
                    position: 'absolute', bottom: '10px', right: '10px',
                    background: 'var(--color-primary)', border: 'none', borderRadius: '6px',
                    width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', opacity: loading || !prompt.trim() ? 0.5 : 1
                  }}>
                  {loading ? '...' : <ChevronRight size={18} color="#050912" />}
                </button>
             </div>
          </div>
        </div>

        {/* Center/Right: Realm Area */}
        <div id="tour-code-editor" style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
          
          {activeTab === 'editor' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
                  {/* Scanline Overlay */}
                  <div style={{ 
                    position: 'absolute', inset: 0, 
                    background: 'linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.25) 50%), linear-gradient(90deg, rgba(255, 0, 0, 0.06), rgba(0, 255, 0, 0.02), rgba(0, 0, 255, 0.06))',
                    backgroundSize: '100% 4px, 3px 100%',
                    pointerEvents: 'none', zIndex: 5,
                    opacity: 0.1
                  }} />
                  
                  <div style={{ 
                    background: '#0a0a0c', // Slightly deeper black for CRT feel
                    flex: 1,
                    padding: '1rem',
                    fontFamily: 'var(--font-mono)',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    overflow: 'auto',
                    position: 'relative',
                    animation: 'flicker 0.15s infinite alternate'
                  }}>
                    {/* Line Numbers Mock */}
                    <div style={{ 
                      position: 'absolute', left: 0, top: 0, bottom: 0, width: '40px', 
                      background: '#252526', borderRight: '1px solid #333', 
                      display: 'flex', flexDirection: 'column', alignItems: 'center', 
                      paddingTop: '1rem', color: '#858585', pointerEvents: 'none'
                    }}>
                      {Array.from({ length: 50 }).map((_, i) => <div key={i}>{i + 1}</div>)}
                    </div>

                    <textarea
                      value={code}
                      onChange={e => setCode(e.target.value)}
                      spellCheck={false}
                      style={{ 
                        width: '100%', height: '100%', background: 'transparent', border: 'none', 
                        outline: 'none', color: '#d4d4d4', resize: 'none', marginLeft: '30px',
                        fontFamily: 'var(--font-mono)', fontSize: '13px'
                      }}
                    />
                  </div>
                  
                  {/* Console Area */}
                  <div style={{ height: '180px', background: 'rgba(5,9,18,0.95)', borderTop: '2px solid #252526', padding: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', opacity: 0.6 }}>
                      <Terminal size={14} />
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}>Serial Monitor / Output</span>
                    </div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--color-primary)', opacity: 0.8 }}>
                      {serialLog.length === 0 ? (
                        <>
                          [INFO] Studio Environment v1.0.0 Initialized.<br />
                          [INFO] Target: ESP32-WROOM-32 Ready.<br />
                          {loading && <span style={{ color: 'var(--color-secondary)' }}>[BUILD] Compiling AI Generated Script...</span>}
                          {!loading && <span style={{ opacity: 0.5 }}>Waiting for serial sync...</span>}
                        </>
                      ) : (
                        serialLog.map((line, idx) => <div key={idx}>{line}</div>)
                      )}
                    </div>
                  </div>
            </div>
          )}

          {activeTab === 'analyzer' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#0a0a0c', padding: '2rem' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <Activity size={24} color="#ff3366" />
              <h2 style={{ fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: 800, color: '#ff3366' }}>AI Serial Analyzer (Debugger)</h2>
            </div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', fontSize: isMobile ? '0.75rem' : '0.85rem' }}>
              Code compiles but the hardware is crashing? Paste your Serial Monitor output or Crash Exception below.
            </p>


                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <textarea 
                    value={logText}
                    onChange={e => setLogText(e.target.value)}
                    placeholder="Paste serial monitor output here... (e.g., Guru Meditation Error, stack trace, or WDT reset)"
                    style={{ 
                      width: '100%', height: '200px', background: 'rgba(18, 16, 16, 0.8)', border: '1px solid #ff3366',
                      borderRadius: '8px', padding: '1rem', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', fontSize: '0.8rem',
                      outline: 'none', resize: 'vertical'
                    }}
                  />
                  
                  <button 
                    onClick={handleAnalyzeLog}
                    disabled={analyzingLog || !logText.trim()}
                    style={{ 
                      background: '#ff3366', color: '#fff', border: 'none', borderRadius: '8px',
                      padding: '0.75rem 1.5rem', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
                      alignSelf: 'flex-start', opacity: analyzingLog || !logText.trim() ? 0.5 : 1,
                      display: 'flex', alignItems: 'center', gap: '0.5rem'
                    }}>
                    {analyzingLog ? 'Analyzing Stack Trace...' : 'Run Diagnostics'} <Activity size={16} />
                  </button>

                  {diagnosis && (
                    <div style={{ 
                      marginTop: '1.5rem', padding: '1.5rem', background: 'rgba(255, 51, 102, 0.1)', 
                      border: '1px solid rgba(255, 51, 102, 0.3)', borderRadius: '12px' 
                    }}>
                      <h4 style={{ color: '#ff3366', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                         <Zap size={16} /> Diagnostic Report
                      </h4>
                      <div dangerouslySetInnerHTML={{ __html: diagnosis.replace(/\n/g, '<br/>') }} style={{ fontSize: '0.9rem', lineHeight: 1.6 }} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {activeTab === 'n8n' && (
            <div style={{ flex: 1, padding: '2rem', overflowY: 'auto' }}>
                <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <Layers size={24} color="var(--color-purple)" />
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 800 }}>n8n + Hardware Bridge</h2>
                  </div>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
                    Connect your ESP32/Arduino to the world using n8n. Use Webhooks or MQTT nodes to bridge software and hardware.
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>

                    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '1.5rem' }}>
                      <h4 style={{ color: 'var(--color-purple)', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <Package size={16} /> MQTT Payload
                      </h4>
                      <div className="code-block" style={{ fontSize: '0.75rem', background: '#050912' }}>
                        {`{
  "device": "esp32-01",
  "status": "online",
  "sensors": {
    "temp": 24.5,
    "humidity": 60
  }
}`}
                      </div>
                      <p style={{ fontSize: '0.75rem', marginTop: '1rem', color: 'var(--text-muted)' }}>
                        Configure your n8n MQTT node to listen on topic: <code>diyelem/data</code>
                      </p>
                    </div>

                    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '1.5rem' }}>
                      <h4 style={{ color: 'var(--color-secondary)', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <Zap size={16} /> Webhook Trigger
                      </h4>
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                        Send commands to your device via n8n Webhook:
                      </p>
                      <div className="code-block" style={{ fontSize: '0.75rem', background: '#050912' }}>
                        {`POST /webhook/esp-control
{
  "action": "LED_ON"
}`}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div style={{ padding: '1.5rem', background: 'rgba(168,85,247,0.05)', border: '1px solid rgba(168,85,247,0.2)', borderRadius: '16px' }}>
                      <h4 style={{ marginBottom: '0.5rem', color: 'var(--color-purple)' }}>The One-Flow Package</h4>
                      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '1.25rem' }}>
                        Generate a complete n8n workflow JSON that matches your current hardware logic. This includes pre-configured MQTT topics, authentication headers, and data parsing nodes.
                      </p>
                      <button 
                        onClick={handleGenerateN8N}
                        disabled={n8nLoading}
                        style={{
                          background: 'var(--color-purple)', color: '#fff', border: 'none', borderRadius: '8px',
                          padding: '0.75rem 1.5rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem',
                          display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: n8nLoading ? 0.6 : 1
                        }}>
                        {n8nLoading ? 'Building Bridge...' : 'Generate n8n Node-Set'} <Layers size={16} />
                      </button>

                      {n8nNodes && (
                        <div style={{ marginTop: '1.5rem' }}>
                           <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                              <span style={{ fontSize: '0.7rem', color: 'var(--color-purple)', fontWeight: 800 }}>GENERATED N8N NODES</span>
                              <button 
                                onClick={() => navigator.clipboard.writeText(n8nNodes)}
                                style={{ background: 'transparent', border: '1px solid rgba(168,85,247,0.3)', color: 'var(--color-purple)', fontSize: '0.65rem', padding: '0.2rem 0.5rem', cursor: 'pointer', borderRadius: '4px' }}
                              >COPY JSON</button>
                           </div>
                           <textarea 
                              readOnly 
                              value={n8nNodes} 
                              style={{ 
                                width: '100%', height: '150px', background: 'rgba(5,9,18,0.8)', border: '1px solid rgba(168,85,247,0.3)',
                                borderRadius: '8px', padding: '0.75rem', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', fontSize: '0.7rem',
                                outline: 'none', resize: 'none'
                              }}
                           />
                        </div>
                      )}
                    </div>

                    <div style={{ padding: '1.25rem', border: '1px solid var(--border-color)', borderRadius: '12px', background: 'rgba(255,255,255,0.02)' }}>
                      <h5 style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Why use the Bridge?</h5>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                        n8n allows you to create complex automations that react to hardware events. 
                        If your ESP32 detects a leak, n8n can send a Slack message, log to Google Sheets, 
                        and shut off a valve via another MQTT device — all without writing backend code.
                      </p>
                    </div>
                  </div>
                </div>
            </div>
          )}

          {activeTab === 'wiring' && (
            <div style={{ flex: 1, overflowY: 'auto', background: '#0a0a0c' }}>
               <Schematics isEmbedded={true} />
            </div>
          )}

          {activeTab === 'bom' && (
            <div style={{ flex: 1, padding: '2rem', overflowY: 'auto', background: '#0a0a0c' }}>
              <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                  <ShoppingCart size={24} color="var(--color-warning, #ffaa00)" />
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--color-warning, #ffaa00)' }}>Verified BOM Wizard</h2>
                </div>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                  Hate searching for parts? ElectrAI will scan your code, identify the required hardware, and generate a 1-click shopping list.
                </p>

                {bomList.length === 0 ? (
                  <div style={{ padding: '2rem', border: '1px dashed var(--border-color)', borderRadius: '12px', textAlign: 'center' }}>
                    <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>No Bill of Materials generated yet.</p>
                    <button 
                      onClick={handleGenerateBOM}
                      disabled={bomLoading}
                      style={{ 
                        background: 'var(--color-warning, #ffaa00)', color: '#000', border: 'none', borderRadius: '8px',
                        padding: '0.75rem 1.5rem', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
                        opacity: bomLoading ? 0.5 : 1
                      }}>
                      {bomLoading ? 'Scanning Code...' : 'Generate BOM from Code'}
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Design Total</span>
                          <span style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--color-warning, #ffaa00)' }}>
                            ${bomList.reduce((acc, item) => acc + (parseFloat(item.estPrice.replace('$', '')) * item.qty), 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.4rem' }}>
                          <button 
                            onClick={async () => {
                              if (window.confirm("Initialize DIYMechStore Virtual Checkout? Parts will be verified via Qdrant Vector Search & graph-matched for your project.")) {
                                try {
                                  alert("Connecting to DIYMechStore.com Gateway... (Shopify Keys Handshake)");
                                  // Logic: Call backend to create Shopify/DIYMechStore cart
                                  // const cart = await api.studio.createStorefrontCart(bomList);
                                  // window.open(cart.url, '_blank');
                                } catch (e) {
                                  alert("DIYMechStore connection failed. Please try again.");
                                }
                              }
                            }}
                            style={{ 
                              background: 'var(--color-primary)', color: '#000', border: 'none', borderRadius: '6px',
                              padding: '0.5rem 1rem', fontSize: '0.8rem', fontWeight: 800, cursor: 'pointer',
                              boxShadow: '0 0 15px rgba(0,255,136,0.3)'
                            }}>
                            1-CLICK DIYMECHSTORE BUNDLE
                          </button>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', color: 'var(--color-primary)', fontSize: '0.7rem', fontWeight: 700 }}>
                            <Zap size={10} fill="var(--color-primary)" /> 10% ECOSYSTEM DISCOUNT AUTO-APPLIED
                          </div>
                        </div>
                        <button 
                          onClick={handleGenerateBOM}
                          disabled={bomLoading}
                          style={{ 
                            background: 'transparent', color: 'var(--color-warning, #ffaa00)', border: '1px solid var(--color-warning, #ffaa00)', borderRadius: '6px',
                            padding: '0.4rem 0.8rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                            opacity: bomLoading ? 0.5 : 1
                          }}>
                          {bomLoading ? '...' : 'Refresh Scan'}
                        </button>
                      </div>
                    </div>
                     <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                            <th style={{ padding: '0.75rem' }}>Component</th>
                            <th style={{ padding: '0.75rem' }}>Type</th>
                            <th style={{ padding: '0.75rem' }}>Qty</th>
                            <th style={{ padding: '0.75rem' }}>Est. Price</th>
                            <th style={{ padding: '0.75rem' }}>Purchase</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bomList.map((item, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: idx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent', fontSize: '0.85rem' }}>
                              <td style={{ padding: '1rem 0.75rem' }}>
                                <div style={{ fontWeight: 600, color: 'var(--color-warning, #ffaa00)' }}>{item.name}</div>
                                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>{item.description}</div>
                              </td>
                              <td style={{ padding: '0.75rem', color: 'var(--text-secondary)' }}>{item.type}</td>
                              <td style={{ padding: '0.75rem' }}>{item.qty}</td>
                              <td style={{ padding: '0.75rem', fontWeight: 700 }}>{item.estPrice}</td>
                              <td style={{ padding: '0.75rem' }}>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                   <button style={{ background: 'var(--color-primary)', color: '#000', border: 'none', padding: '0.3rem 0.6rem', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer', fontWeight: 800 }}>DIY-MECH</button>
                                   <button style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--text-muted)', border: '1px solid var(--border-color)', padding: '0.3rem 0.6rem', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer', fontWeight: 600 }}>Options</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                     </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'power' && (
            <div style={{ flex: 1, padding: '2rem', overflowY: 'auto', background: '#0a0a0c' }}>
               <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <Flame size={24} color="#ff5500" />
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#ff5500' }}>"Will It Burn?" Power Analyzer</h2>
                  </div>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
                    AI-driven power profiling and thermal risk assessment. Never melt a regulator again.
                  </p>

                  {!powerReport ? (
                    <div style={{ border: '1px dashed #ff550055', borderRadius: '16px', padding: '3rem', textAlign: 'center' }}>
                       <button 
                        onClick={handleAnalyzePower}
                        disabled={powerLoading}
                        style={{ background: '#ff5500', color: '#fff', border: 'none', borderRadius: '8px', padding: '1rem 2rem', fontWeight: 700, cursor: 'pointer' }}>
                        {powerLoading ? 'Calculating Power Draw...' : 'Analyze Power Profile'}
                       </button>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                       <div style={{ background: 'rgba(255,85,0,0.05)', border: '1px solid #ff550033', borderRadius: '16px', padding: '1.5rem' }}>
                          <h4 style={{ color: '#ff5500', marginBottom: '1rem' }}>Current Consumption</h4>
                          <div style={{ fontSize: '2rem', fontWeight: 800 }}>{powerReport?.peak_current}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Peak Demand</div>
                          <div style={{ marginTop: '1rem', borderTop: '1px solid #ff550022', paddingTop: '1rem' }}>
                             <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                               <span>Idle Current:</span>
                               <span style={{ fontWeight: 600 }}>{powerReport?.idle_current}</span>
                             </div>
                             <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                               <span>Safe Voltage:</span>
                               <span style={{ fontWeight: 600 }}>{powerReport?.safe_voltage}</span>
                             </div>
                          </div>
                       </div>
                       <div style={{ background: 'rgba(255,85,0,0.05)', border: '1px solid #ff550033', borderRadius: '16px', padding: '1.5rem' }}>
                          <h4 style={{ color: '#ff5500', marginBottom: '1rem' }}>Thermal Risk</h4>
                          <div style={{ fontSize: '2rem', fontWeight: 800, color: powerReport?.thermal_risk === 'High' ? '#ff3366' : '#00ff88' }}>{powerReport?.thermal_risk}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Overall Assessment</div>
                          <div style={{ marginTop: '1rem', borderTop: '1px solid #ff550022', paddingTop: '1rem' }}>
                             <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                {powerReport?.warnings.map((w: string) => (
                                  <span key={w} style={{ background: '#ff550022', color: '#ff5500', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem' }}>{w}</span>
                                ))}
                             </div>
                          </div>
                       </div>
                       <div style={{ gridColumn: 'span 2', background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '1.5rem', textAlign: 'center' }}>
                          <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>Estimated Battery Life (2000mAh)</div>
                          <div style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--color-primary)' }}>{powerReport?.est_battery_life_2000mah}</div>
                       </div>
                    </div>
                  )}
               </div>
            </div>
          )}

          {activeTab === 'fleet' && (
            <div style={{ flex: 1, padding: '2rem', overflowY: 'auto', background: '#0a0a0c' }}>
               <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <Globe size={24} color="var(--color-secondary)" />
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--color-secondary)' }}>OTA Fleet Management</h2>
                  </div>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
                    Push updates to your distributed ESP32/Arduino fleet with one click. 
                  </p>

                  <div style={{ border: '1px solid var(--border-color)', borderRadius: '16px', overflow: 'hidden' }}>
                     <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: 'rgba(255,255,255,0.05)' }}>
                           <tr style={{ textAlign: 'left', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                              <th style={{ padding: '1rem' }}>Device ID</th>
                              <th style={{ padding: '1rem' }}>Status</th>
                              <th style={{ padding: '1rem' }}>Version</th>
                              <th style={{ padding: '1rem' }}>Action</th>
                           </tr>
                        </thead>
                        <tbody>
                           {[
                             { id: 'esp32-sensor-01', status: 'Online', ver: '1.2.0' },
                             { id: 'logger-main-unit', status: 'Offline', ver: '1.1.8' },
                             { id: 'hvac-controller-04', status: 'Online', ver: '1.2.0' }
                           ].map(dev => (
                             <tr key={dev.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                               <td style={{ padding: '1rem', fontWeight: 600 }}>{dev.id}</td>
                               <td style={{ padding: '1rem' }}>
                                 <span style={{ color: dev.status === 'Online' ? '#00ff88' : '#666', fontSize: '0.75rem' }}>● {dev.status}</span>
                               </td>
                               <td style={{ padding: '1rem', color: 'var(--text-secondary)' }}>v{dev.ver}</td>
                               <td style={{ padding: '1rem' }}>
                                  <button style={{ background: 'var(--bg-surface-2)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', padding: '0.3rem 0.6rem', borderRadius: '4px', fontSize: '0.7rem' }}>Push Update</button>
                               </td>
                             </tr>
                           ))}
                        </tbody>
                     </table>
                  </div>
                  <div style={{ marginTop: '2rem', padding: '1.5rem', background: 'rgba(0,180,255,0.05)', border: '1px solid rgba(0,180,255,0.2)', borderRadius: '16px' }}>
                     <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                       <strong>Pro Fleet Tip:</strong> Enable OTA-by-default in your Script Architect settings to automatically include update logic in every generated script.
                     </p>
                  </div>
               </div>
            </div>
          )}
          {activeTab === 'rescue' && (
            <div style={{ flex: 1, padding: '2rem', overflowY: 'auto', background: '#0a0a0c' }}>
               <div style={{ maxWidth: '700px', margin: '0 auto', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <HelpCircle size={24} color="#fff" />
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 800 }}>Bench Rescue Mode</h2>
                  </div>
                  
                  {!rescueResult && rescueStep < 5 ? (
                    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '2rem' }}>
                       <div style={{ marginBottom: '2rem' }}>
                          <div style={{ color: 'var(--color-primary)', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.5rem' }}>Step {rescueStep + 1} of 5</div>
                          <h3 style={{ fontSize: '1.25rem' }}>
                             {rescueStep === 0 && "What board are you using?"}
                             {rescueStep === 1 && "What is the primary symptom?"}
                             {rescueStep === 2 && "Any physical signs of distress?"}
                             {rescueStep === 3 && "Have you measured the power rails?"}
                             {rescueStep === 4 && "Paste your Serial Monitor / Log output (optional)"}
                          </h3>
                       </div>

                       <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                          {rescueStep === 0 && ["ESP32-WROOM", "ESP8266", "Arduino Uno/Nano", "STM32/Other"].map(opt => (
                            <button key={opt} onClick={() => { setRescueAnswers({...rescueAnswers, board: opt}); setRescueStep(1); }} className="rescue-opt-btn">{opt}</button>
                          ))}
                          {rescueStep === 1 && ["No Power / No LEDs", "Bootlooping / Crashing", "No Communications (WiFi/Serial)", "Sensors providing junk data"].map(opt => (
                            <button key={opt} onClick={() => { setRescueAnswers({...rescueAnswers, symptom: opt}); setRescueStep(2); }} className="rescue-opt-btn">{opt}</button>
                          ))}
                          {rescueStep === 2 && ["Gets very hot", "Smell of burning", "Magic smoke appeared", "Looks perfectly fine"].map(opt => (
                            <button key={opt} onClick={() => { setRescueAnswers({...rescueAnswers, physical: opt}); setRescueStep(3); }} className="rescue-opt-btn">{opt}</button>
                          ))}
                          {rescueStep === 3 && ["3.3V stable", "5.0V stable", "Voltage looks low/sagging", "Haven't measured yet"].map(opt => (
                            <button key={opt} onClick={() => { setRescueAnswers({...rescueAnswers, power: opt}); setRescueStep(4); }} className="rescue-opt-btn">{opt}</button>
                          ))}
                          {rescueStep === 4 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                              <textarea 
                                value={rescueAnswers.log || ''} 
                                onChange={e => setRescueAnswers({...rescueAnswers, log: e.target.value})}
                                placeholder="Paste serial output here for deep trace analysis..."
                                style={{ 
                                  width: '100%', height: '120px', background: 'rgba(5,9,18,0.8)', border: '1px solid var(--border-color)',
                                  borderRadius: '8px', padding: '0.75rem', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', fontSize: '0.8rem',
                                  outline: 'none', resize: 'none'
                                }}
                              />
                              <div style={{ display: 'flex', gap: '1rem' }}>
                                <button onClick={() => { setRescueAnswers({...rescueAnswers, log: 'SKIP'}); handleRescueSubmit(); }} className="rescue-opt-btn" style={{ flex: 1, textAlign: 'center' }}>Skip Log Analysis</button>
                                <button onClick={handleRescueSubmit} className="rescue-opt-btn" style={{ flex: 1, textAlign: 'center', background: 'var(--color-primary)', color: '#000', fontWeight: 800 }}>Search Failure Block</button>
                              </div>
                            </div>
                          )}
                       </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                       {rescueLoading ? (
                          <div style={{ textAlign: 'center', padding: '4rem' }}>
                             <Activity size={48} className="animate-spin" style={{ opacity: 0.5, marginBottom: '1rem' }} />
                             <h3>Running Global Diagnostic Sweep...</h3>
                          </div>
                       ) : (
                          <>
                            <div style={{ background: 'rgba(255,51,102,0.1)', border: '1px solid #ff336644', borderRadius: '16px', padding: '2rem' }}>
                               <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                                  <AlertTriangle size={32} color="#ff3366" />
                                  <div>
                                     <div style={{ color: '#ff3366', fontWeight: 800, fontSize: '0.8rem', textTransform: 'uppercase' }}>Likely Failure Detected</div>
                                     <div style={{ fontSize: '1.5rem', fontWeight: 900 }}>{rescueResult?.likely_failure}</div>
                                  </div>
                                  <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                                     <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Confidence</div>
                                     <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{rescueResult?.confidence}</div>
                                  </div>
                               </div>
                               <p style={{ lineHeight: 1.6, color: 'var(--text-secondary)' }}>{rescueResult?.analysis}</p>
                            </div>

                            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '2rem' }}>
                               <h4 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <Zap size={18} color="var(--color-primary)" /> Verification Measurements
                               </h4>
                               <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                  {rescueResult?.measurements.map((m: any, i: number) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: '8px' }}>
                                       <div style={{ flex: 1 }}>
                                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{m.point}</div>
                                          <div style={{ fontWeight: 600 }}>{m.expected}</div>
                                       </div>
                                       <div style={{ fontSize: '0.7rem', color: 'var(--color-primary)', background: 'rgba(0,255,136,0.1)', padding: '0.2rem 0.5rem', borderRadius: '4px' }}>
                                          {m.tool}
                                       </div>
                                    </div>
                                  ))}
                               </div>
                            </div>

                            <div style={{ textAlign: 'center', padding: '2rem', border: '1px solid #ffcc00', borderRadius: '16px', background: 'rgba(255,204,0,0.05)' }}>
                               <h4 style={{ color: '#ffcc00', marginBottom: '0.5rem' }}>Still Stuck?</h4>
                               <p style={{ fontSize: '0.85rem', marginBottom: '1.5rem' }}>Escalate this project to a Human Expert for a 15-minute rescue call.</p>
                               <button style={{ background: '#ffcc00', color: '#000', border: 'none', borderRadius: '8px', padding: '0.75rem 1.5rem', fontWeight: 800, cursor: 'pointer' }}>
                                  Request Expert Handoff ($20.00)
                               </button>
                            </div>

                            <button onClick={() => { setRescueResult(null); setRescueStep(0); }} style={{ alignSelf: 'center', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', textDecoration: 'underline' }}>
                               Restart Rescue Flow
                            </button>
                          </>
                       )}
                    </div>
                  )}
               </div>
            </div>
          )}

          {activeTab === 'driver' && (
            <div style={{ flex: 1, padding: '2rem', overflowY: 'auto', background: '#0a0a0c' }}>
               <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <BookOpen size={24} color="#ffaa00" />
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#ffaa00' }}>Driver Lab (Datasheet-to-Library)</h2>
                  </div>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
                    Paste register map text or upload an excerpt from a datasheet to generate a custom, hardened Arduino library (.h/.cpp).
                  </p>

                  <div style={{ padding: '2rem', border: '1px dashed #ffaa0055', borderRadius: '16px', background: 'rgba(255,170,0,0.02)', textAlign: 'center' }}>
                     <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                        The AI will analyze the I2C/SPI registers, bitmasks, and timing requirements.
                     </p>
                     <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <textarea 
                           placeholder="Paste register map, register descriptions, or initialization sequence from PDF..."
                           style={{ 
                              width: '100%', height: '180px', background: 'rgba(18, 16, 16, 0.8)', border: '1px solid #ffaa0033',
                              borderRadius: '8px', padding: '1rem', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', fontSize: '0.75rem'
                           }}
                        />
                        <button style={{ 
                           background: '#ffaa00', color: '#000', border: 'none', borderRadius: '8px', 
                           padding: '0.75rem 1.5rem', fontWeight: 700, cursor: 'pointer'
                        }}>
                           Generate Library Driver (1 Free Credit)
                        </button>
                     </div>
                  </div>

                  <div style={{ marginTop: '2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                     <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '1rem' }}>
                        <h5 style={{ color: '#ffaa00', marginBottom: '0.5rem' }}>Recent Generations</h5>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>No drivers generated in this session.</div>
                     </div>
                     <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '1rem' }}>
                        <h5 style={{ color: 'var(--color-primary)', marginBottom: '0.5rem' }}>Library Specs</h5>
                        <ul style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0, paddingLeft: '1.2rem' }}>
                           <li>Hardened I2C/SPI comms</li>
                           <li>Bespoke bitfield macros</li>
                           <li>Initialization & Scale methods</li>
                        </ul>
                     </div>
                  </div>
               </div>
            </div>
          )}

          {activeTab === 'fab' && (
            <div style={{ flex: 1, padding: '2rem', overflowY: 'auto', background: '#0a0a0c' }}>
               <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <Layers size={24} color="var(--color-secondary)" />
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--color-secondary)' }}>Fab Hub (PCB Fabrication)</h2>
                  </div>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
                    Convert your project into a production-ready PCB order. Get fabrication specs and direct affiliate links to top manufacturers.
                  </p>

                  {!fabPackage ? (
                     <div style={{ padding: '3rem', border: '1px dashed var(--color-secondary)', borderRadius: '16px', background: 'rgba(0,255,255,0.02)', textAlign: 'center' }}>
                        <button 
                          onClick={handleGenerateFab}
                          disabled={fabLoading}
                          style={{ 
                            background: 'var(--color-secondary)', color: '#000', border: 'none', borderRadius: '8px', 
                            padding: '1rem 2rem', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 auto'
                          }}
                        >
                          {fabLoading ? <Activity size={20} className="animate-spin" /> : <Layers size={20} />}
                          Generate Fab Specification
                        </button>
                        <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                          AI will analyze code & wiring to determine board layers, trace width, and dimensions.
                        </p>
                     </div>
                  ) : (
                     <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                           {fabPackage.specs && Object.entries(fabPackage.specs).map(([k, v]: [string, any]) => (
                              <div key={k} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '1rem' }}>
                                 <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>{k.replace(/_/g, ' ')}</div>
                                 <div style={{ fontWeight: 700, color: 'var(--color-secondary)' }}>{v}</div>
                              </div>
                           ))}
                        </div>

                        <div style={{ background: 'rgba(0,255,136,0.05)', border: '1px solid #00ff8844', borderRadius: '16px', padding: '2rem' }}>
                           <h4 style={{ color: 'var(--color-primary)', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <ShoppingCart size={18} /> Order Hardware
                           </h4>
                           <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                              {fabPackage.affiliate_links && fabPackage.affiliate_links.map((link: any, i: number) => (
                                 <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '1.25rem', height: '100%', transition: 'all 0.2s' }}>
                                       <div style={{ fontWeight: 800, fontSize: '1.2rem', marginBottom: '0.25rem' }}>{link.name}</div>
                                       <div style={{ fontSize: '0.85rem', color: 'var(--color-primary)' }}>{link.promo}</div>
                                       <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                          Visit Partner <ChevronRight size={14} />
                                       </div>
                                    </div>
                                 </a>
                              ))}
                           </div>
                        </div>

                        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '2rem' }}>
                           <h4 style={{ marginBottom: '1rem' }}>Next Steps</h4>
                           <ul style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', margin: 0, paddingLeft: '1.5rem' }}>
                              {fabPackage.next_steps && fabPackage.next_steps.map((step: string, i: number) => (
                                 <li key={i} style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{step}</li>
                              ))}
                           </ul>
                        </div>

                        <button onClick={() => setFabPackage(null)} style={{ alignSelf: 'center', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', textDecoration: 'underline' }}>
                          Reset Fab Project
                        </button>
                     </div>
                  )}
               </div>
            </div>
          )}

          {activeTab === 'flasher' && (
            <div style={{ flex: 1, padding: '2rem', overflowY: 'auto', background: '#0a0a0c' }}>
               <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <Cpu size={24} color="var(--color-primary)" />
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--color-primary)' }}>ElectrAI Universal Flasher</h2>
                  </div>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
                    Deploy local firmware builds (.bin, .hex) directly to your 3D printer or microcontroller via Web Serial.
                  </p>

                  <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '2.5rem', textAlign: 'center' }}>
                     {!isConnected ? (
                       <div style={{ color: '#ff3366', marginBottom: '1rem', fontWeight: 600 }}>
                          <AlertTriangle size={20} style={{ marginBottom: '0.5rem' }} /><br />
                          Connect to your device using the SYNC button in the toolbar first.
                       </div>
                     ) : (
                       <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', alignItems: 'center' }}>
                          <div style={{ width: '100%', maxWidth: '400px' }}>
                             <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: '0.5rem', textAlign: 'left' }}>Select Firmware File</label>
                             <input 
                               type="file" 
                               accept=".bin,.hex"
                               onChange={e => setFirmwareFile(e.target.files?.[0] || null)}
                               style={{ 
                                 width: '100%', padding: '0.75rem', background: '#050912', border: '1px solid var(--border-color)', 
                                 borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.85rem'
                               }}
                             />
                          </div>

                          {firmwareFile && (
                            <div style={{ width: '100%', maxWidth: '400px', padding: '1rem', background: 'rgba(0,255,136,0.05)', border: '1px solid rgba(0,255,136,0.2)', borderRadius: '12px' }}>
                               <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--color-primary)' }}>{firmwareFile.name}</div>
                               <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Size: {(firmwareFile.size / 1024).toFixed(1)} KB</div>
                            </div>
                          )}

                          <button 
                            onClick={handleFlashFirmware}
                            disabled={flashing || !firmwareFile}
                            style={{ 
                              background: 'var(--color-primary)', color: '#000', border: 'none', borderRadius: '8px', 
                              padding: '1rem 2.5rem', fontWeight: 800, cursor: 'pointer', fontSize: '1rem',
                              opacity: (flashing || !firmwareFile) ? 0.5 : 1, transition: 'all 0.2s',
                              boxShadow: '0 0 20px rgba(0,255,136,0.3)'
                            }}
                          >
                            {flashing ? 'FLASHING...' : 'FLASH TO DEVICE'}
                          </button>

                          {flashing && (
                            <div style={{ width: '100%', maxWidth: '400px', marginTop: '1rem' }}>
                               <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '0.4rem' }}>
                                  <span style={{ color: 'var(--color-primary)' }}>Progress</span>
                                  <span>{flashProgress}%</span>
                               </div>
                               <div style={{ width: '100%', height: '8px', background: '#050912', borderRadius: '4px', overflow: 'hidden' }}>
                                  <div style={{ width: `${flashProgress}%`, height: '100%', background: 'var(--color-primary)', transition: 'width 0.1s linear' }} />
                               </div>
                            </div>
                          )}
                       </div>
                     )}
                  </div>

                  <div style={{ marginTop: '2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                     <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '1.5rem' }}>
                        <h4 style={{ color: 'var(--color-primary)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>Anycubic / 3D Printer Support</h4>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                           ElectrAI Studio supports flashing pre-compiled Marlin and Klipper binaries. 
                           For Anycubic boards (TriGorilla/STM32), ensure you select the correct serial port and baud rate (usually 115200 or 250000).
                        </p>
                     </div>
                     <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '1.5rem' }}>
                        <h4 style={{ color: 'var(--color-secondary)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>Safety Notice</h4>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                           Interrupting a firmware flash can brick your device. Ensure a stable USB connection and do not close the tab until the progress reach 100%.
                        </p>
                     </div>
                  </div>
               </div>
            </div>
          )}

        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .studio-btn {
          width: 32px; height: 32px;
          background: var(--bg-surface);
          border: 1px solid var(--border-color);
          border-radius: 6px;
          display: flex; alignItems: center; justifyContent: center;
          color: var(--text-muted);
          cursor: pointer; transition: all 0.2s;
        }
        .studio-btn:hover {
          border-color: var(--color-primary);
          color: var(--text-primary);
        }
        .rescue-opt-btn {
          background: var(--bg-surface-2);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          padding: 1rem;
          color: var(--text-primary);
          text-align: left;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .rescue-opt-btn:hover {
          border-color: var(--color-primary);
          background: rgba(0,255,136,0.05);
        }
        @keyframes flicker {
          0% { opacity: 0.97; }
          5% { opacity: 0.95; }
          10% { opacity: 0.9; }
          15% { opacity: 0.98; }
          20% { opacity: 0.95; }
          25% { opacity: 0.94; }
          30% { opacity: 0.93; }
          35% { opacity: 0.95; }
          40% { opacity: 0.9; }
          45% { opacity: 0.96; }
          50% { opacity: 0.95; }
          55% { opacity: 0.94; }
          60% { opacity: 0.93; }
          65% { opacity: 0.95; }
          70% { opacity: 0.9; }
          75% { opacity: 0.91; }
          80% { opacity: 0.95; }
          85% { opacity: 0.94; }
          90% { opacity: 0.93; }
          95% { opacity: 0.95; }
          100% { opacity: 0.98; }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        
        @keyframes pulse {
          0% { opacity: 0.5; }
          50% { opacity: 1; }
          100% { opacity: 0.5; }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .apply-editor-btn:hover {
            box-shadow: 0 0 20px rgba(0, 255, 136, 0.8) !important;
            transform: scale(1.02);
        }
        .studio-btn:hover {
            opacity: 0.8;
            transform: translateY(-1px);
        }
      ` }} />
      <GuidedTour 
        steps={TOUR_STEPS}
        isOpen={isTourOpen}
        onClose={() => {
          setIsTourOpen(false);
          localStorage.setItem('diy_mech_tour_seen', 'true');
        }}
      />
    </div>
    </div>
  );
}
